import { Card, DeckClassification, classifyDeckWords } from '@lingoping/core';
import { StorageManager } from '../storage/index.js';
import type { CloudDeckWord } from './cloudDecks.js';

/** What the learner already has: their cards and, lower-cased, every word on the known list. */
export interface LearnerState {
  cards: Card[];
  known: Set<string>;
}

export const EMPTY_LEARNER: LearnerState = { cards: [], known: new Set() };

export async function loadLearnerState(): Promise<LearnerState> {
  const [cards, knownWords] = await Promise.all([
    StorageManager.getCards(),
    StorageManager.getKnownWords(),
  ]);
  return { cards, known: new Set(knownWords.map((w) => w.lemma.trim().toLowerCase())) };
}

/** The one rule for where the learner stands on a deck's words (`classifyDeckWords` in core). */
export function classifyDeck(
  words: CloudDeckWord[],
  learner: LearnerState,
  deferrals: Record<string, string> = {}
): DeckClassification {
  return classifyDeckWords(
    words,
    learner.cards,
    learner.known,
    new Map(Object.entries(deferrals)),
    // A deck may write a word differently from its dictionary form ("Die Speisekarte").
    (word) => [(word as CloudDeckWord).word?.lemma, word.lemma]
  );
}

export interface DeckProgress {
  total: number;
  /** Chosen to learn: words with a card, started or waiting to be started. */
  learning: number;
  known: number;
  /** Not decided yet. */
  toGo: number;
}

export function deckProgress(classification: DeckClassification): DeckProgress {
  const { counts } = classification;
  return {
    total: classification.words.length,
    learning: counts.learning + counts.waiting,
    known: counts.known,
    toGo: counts.undecided,
  };
}
